import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Edit } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { Warehouse, CreateWarehouseRequest, UpdateWarehouseRequest } from '../../../../types/warehouse';
import { warehouseService } from '../../../../services/warehouseService';
import { stcBinService } from '../../../../services/stcBinService';
import { AuthService } from '../../../../services/authService';
import { NumericInput } from '../../../common/inputs';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface WarehouseAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface WarehouseFormData {
  warehouseID: string;
  warehouseDescr: string;
  externalID: string;
  useAWYesNo: boolean;
  useLocationYesNo: boolean;
  erpAWYesNo: boolean;
  inStockCountingMode: boolean;
  // Crossdock (TW 36395306)
  grCrossDockingYesNo: boolean;
  grCrossDockingNumOfDays: string;
  defaultGRCDBinCode: string;
}

const EMPTY_FORM: WarehouseFormData = {
  warehouseID: '',
  warehouseDescr: '',
  externalID: '',
  useAWYesNo: true,
  useLocationYesNo: true,
  erpAWYesNo: false,
  inStockCountingMode: false,
  grCrossDockingYesNo: false,
  grCrossDockingNumOfDays: '0',
  defaultGRCDBinCode: ''
};

export const WarehouseApp: React.FC<WarehouseAppProps> = ({ onClose, onBack }) => {
  const { t } = useTranslation();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | null>(null);
  const [formData, setFormData] = useState<WarehouseFormData>(EMPTY_FORM);
  const [crossDockBins, setCrossDockBins] = useState<string[]>([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await warehouseService.getWarehouses();
      setWarehouses(data);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setError(t('manager.modules.adminHost.warehouseApp.loadError', {}, 'Feil ved lasting av lagersted data'));
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingWarehouse(null);
    setFormData(EMPTY_FORM);
    setCrossDockBins([]); // Nytt lager har ingen lokasjoner ennå
    setFormError(null);
    setSuccessMessage(null);
    setShowModal(true);
  };

  const openEditModal = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      warehouseID: warehouse.warehouseID,
      warehouseDescr: warehouse.warehouseDescr,
      externalID: warehouse.externalID || '',
      useAWYesNo: warehouse.useAWYesNo,
      useLocationYesNo: warehouse.useLocationYesNo,
      erpAWYesNo: warehouse.erpAWYesNo,
      inStockCountingMode: warehouse.inStockCountingMode,
      grCrossDockingYesNo: warehouse.grCrossDockingYesNo ?? false,
      grCrossDockingNumOfDays: String(warehouse.grCrossDockingNumOfDays ?? 0),
      defaultGRCDBinCode: warehouse.defaultGRCDBinCode ?? ''
    });
    setFormError(null);
    setSuccessMessage(null);
    setShowModal(true);
    loadCrossDockBins(warehouse);
  };

  const loadCrossDockBins = async (warehouse: Warehouse) => {
    setCrossDockBins([]);
    try {
      const bins = await stcBinService.getCrossDockLocations(warehouse.companyNo, warehouse.warehouseID);
      setCrossDockBins(bins.map(b => b.binCode));
    } catch (err) {
      // Kun for nedtrekkslisten — feiler oppslaget, kan resten av lageret fortsatt lagres
      console.error('Error loading crossdock locations:', err);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWarehouse(null);
    setFormError(null);
    setSuccessMessage(null);
  };

  const handleInputChange = (field: keyof WarehouseFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null); // Clear error when user starts typing
  };

  const validateForm = (): string | null => {
    if (!formData.warehouseID.trim()) {
      return t('manager.modules.adminHost.warehouseApp.validation.idRequired', {}, 'Lagersted ID er påkrevd');
    }
    if (!formData.warehouseDescr.trim()) {
      return t('manager.modules.adminHost.warehouseApp.validation.descriptionRequired', {}, 'Beskrivelse er påkrevd');
    }
    // Check for duplicate warehouse ID
    const duplicateWarehouse = warehouses.find(w => 
      w.warehouseID.toLowerCase() === formData.warehouseID.toLowerCase()
    );
    
    if (duplicateWarehouse) {
      // If creating or editing and the duplicate is not the current warehouse being edited
      if (!editingWarehouse || duplicateWarehouse.warehouseID !== editingWarehouse.warehouseID) {
        return t('manager.modules.adminHost.warehouseApp.validation.idExists', {}, 'Lagersted ID finnes allerede');
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const userContext = AuthService.getUserDataFromToken();
      if (!userContext) {
        throw new Error(t('manager.modules.adminHost.warehouseApp.noUserContext', {}, 'Ingen brukerkontext tilgjengelig'));
      }

      if (editingWarehouse) {
        // Update existing warehouse
        const updateRequest: UpdateWarehouseRequest = {
          warehouseID: formData.warehouseID.trim(),
          warehouseDescr: formData.warehouseDescr.trim(),
          externalID: formData.externalID.trim() || undefined,
          useAWYesNo: formData.useAWYesNo,
          useLocationYesNo: formData.useLocationYesNo,
          erpAWYesNo: formData.erpAWYesNo,
          inStockCountingMode: formData.inStockCountingMode,
          grCrossDockingYesNo: formData.grCrossDockingYesNo,
          grCrossDockingNumOfDays: parseInt(formData.grCrossDockingNumOfDays, 10) || 0,
          defaultGRCDBinCode: formData.defaultGRCDBinCode // tom streng = fjern forvalget
        };

        await warehouseService.updateWarehouse(
          userContext.companyNo,
          editingWarehouse.warehouseID,
          updateRequest
        );

        setSuccessMessage(t('manager.modules.adminHost.warehouseApp.updateSuccess', {}, 'Lagersted ble oppdatert'));
      } else {
        // Create new warehouse
        const createRequest: CreateWarehouseRequest = {
          warehouseID: formData.warehouseID.trim(),
          warehouseDescr: formData.warehouseDescr.trim(),
          externalID: formData.externalID.trim() || undefined,
          useAWYesNo: formData.useAWYesNo,
          useLocationYesNo: formData.useLocationYesNo,
          erpAWYesNo: formData.erpAWYesNo,
          inStockCountingMode: formData.inStockCountingMode,
          grCrossDockingYesNo: formData.grCrossDockingYesNo,
          grCrossDockingNumOfDays: parseInt(formData.grCrossDockingNumOfDays, 10) || 0
        };

        await warehouseService.createWarehouse(userContext.companyNo, createRequest);
        setSuccessMessage(t('manager.modules.adminHost.warehouseApp.createSuccess', {}, 'Nytt lagersted ble opprettet'));
      }

      // Reload data and close modal after success
      setTimeout(() => {
        loadWarehouses();
        closeModal();
      }, 1000);

    } catch (error) {
      console.error('Error saving warehouse:', error);
      setFormError(error instanceof Error ? error.message : t('manager.modules.adminHost.warehouseApp.saveError', {}, 'Feil ved lagring av lagersted'));
    } finally {
      setFormLoading(false);
    }
  };


  // Create type-safe columns using factory pattern
  const columnFactory = createColumnFactory<Warehouse>();
  
  const columns = [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('manager.modules.adminHost.warehouseApp.columns.warehouseId', {}, 'Lagersted ID'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('warehouseDescr', 'warehouseDescr', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 250,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('externalID', 'externalID', t('manager.modules.adminHost.warehouseApp.columns.externalId', {}, 'Ekstern ID'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createBooleanColumn('useAWYesNo', 'useAWYesNo', t('manager.modules.adminHost.warehouseApp.fields.advancedWarehouse', {}, 'Avansert Lager'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown'
    }),
    columnFactory.createBooleanColumn('useLocationYesNo', 'useLocationYesNo', t('manager.modules.adminHost.warehouseApp.fields.useLocation', {}, 'Bruk Lokasjon'), {
      width: 120,
      sortable: true,
      filterType: 'dropdown'
    }),
    // Crossdock (TW 36395306)
    columnFactory.createBooleanColumn('grCrossDockingYesNo', 'grCrossDockingYesNo', t('manager.modules.adminHost.warehouseApp.crossDock.column', {}, 'Crossdock'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown'
    }),
    columnFactory.createNumberColumn('grCrossDockingNumOfDays', 'grCrossDockingNumOfDays', t('manager.modules.adminHost.warehouseApp.crossDock.numOfDays', {}, 'Antall dager frem i tid'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('defaultGRCDBinCode', 'defaultGRCDBinCode', t('manager.modules.adminHost.warehouseApp.crossDock.defaultBin', {}, 'Forvalgt crossdock-lokasjon'), {
      width: 160,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('companyNo', 'companyNo', t('manager.modules.adminHost.warehouseApp.columns.company', {}, 'Selskap'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminHost.warehouseApp.columns.createdBy', {}, 'Opprettet av'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminHost.warehouseApp.columns.created', {}, 'Opprettet'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminHost.warehouseApp.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminHost.warehouseApp.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createCustomColumn({
      id: 'actions',
      key: 'id' as keyof Warehouse,
      label: t('manager.modules.adminHost.warehouseApp.columns.actions', {}, 'Handlinger'),
      width: 100,
      visible: true,
      sortable: false,
      resizable: false,
      minWidth: 100,
      alignment: 'center',
      type: 'text',
      filterType: 'none',
      renderer: (_, warehouse) => (
        <div className="flex gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(warehouse);
            }}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
            title={t('manager.modules.adminHost.warehouseApp.editWarehouseTitle', {}, 'Rediger lagersted')}
          >
            {t('common.buttons.edit', {}, 'Rediger')}
          </button>
        </div>
      )
    })
  ];

  const handleRowClick = (warehouse: Warehouse) => {
    openEditModal(warehouse);
  };

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">{t('manager.modules.adminHost.warehouseApp.dataLoadError', {}, 'Feil ved lasting av data')}</h3>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={loadWarehouses}
            className="mt-2 px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
          >
            {t('manager.modules.adminHost.common.tryAgain', {}, 'Prøv igjen')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Success Message */}
      {successMessage && (
        <div className="mx-4 mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-green-800 text-sm">{successMessage}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.adminHost.warehouseApp.title', {}, 'Lagersted')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.adminHost.warehouseApp.subtitle', {}, 'Administrer lagersted konfigurasjoner for selskapet')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4">
        <WarehouseTable
          data={warehouses}
          columns={columns}
          loading={loading}
          config={{
            persistenceKey: 'warehouse-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 100,
            compactMode: true, // Enable professional 16px styling
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: false // No row selection needed for read-only view
          }}
          tableActions={[
            {
              id: 'create-warehouse',
              label: t('manager.modules.adminHost.warehouseApp.createNew', {}, 'Opprett ny'),
              icon: Plus,
              onClick: openCreateModal,
              variant: 'primary'
            },
            {
              id: 'refresh-warehouses',
              label: loading ? t('common.status.loading', {}, 'Laster...') : t('common.buttons.refresh', {}, 'Oppdater'),
              icon: RefreshCw,
              onClick: loadWarehouses,
              disabled: () => loading,
              variant: 'secondary'
            }
          ]}
          aria-label={t('manager.modules.adminHost.warehouseApp.tableAriaLabel', {}, 'Lagersted tabell')}
        />
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingWarehouse ? t('manager.modules.adminHost.warehouseApp.editWarehouseTitle', {}, 'Rediger lagersted') : t('manager.modules.adminHost.warehouseApp.createWarehouseTitle', {}, 'Opprett nytt lagersted')}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-xl"
                aria-label={t('manager.modules.adminHost.warehouseApp.closeModalAria', {}, 'Lukk modal')}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="p-6">
              {/* Error Message */}
              {formError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">{successMessage}</p>
                </div>
              )}

              {/* Form Fields */}
              <div className="grid grid-cols-1 gap-4">
                {/* Warehouse ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.warehouseApp.labels.warehouseIdRequired', {}, 'Lagersted ID *')}
                  </label>
                  <input
                    type="text"
                    value={formData.warehouseID}
                    onChange={(e) => handleInputChange('warehouseID', e.target.value)}
                    disabled={formLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    placeholder={t('manager.modules.adminHost.warehouseApp.placeholders.warehouseId', {}, 'Skriv inn lagersted ID')}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('manager.modules.adminHost.warehouseApp.hints.uniqueId', {}, 'Unik identifikator for lagerstedet')}
                  </p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.warehouseApp.labels.descriptionRequired', {}, 'Beskrivelse *')}
                  </label>
                  <input
                    type="text"
                    value={formData.warehouseDescr}
                    onChange={(e) => handleInputChange('warehouseDescr', e.target.value)}
                    disabled={formLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    placeholder={t('manager.modules.adminHost.warehouseApp.placeholders.description', {}, 'Skriv inn beskrivelse')}
                    required
                  />
                </div>

                {/* External ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.adminHost.warehouseApp.columns.externalId', {}, 'Ekstern ID')}
                  </label>
                  <input
                    type="text"
                    value={formData.externalID}
                    onChange={(e) => handleInputChange('externalID', e.target.value)}
                    disabled={formLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    placeholder={t('manager.modules.adminHost.warehouseApp.placeholders.externalId', {}, 'Ekstern system referanse (valgfri)')}
                  />
                </div>

                {/* Boolean Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useAWYesNo"
                      checked={formData.useAWYesNo}
                      onChange={(e) => handleInputChange('useAWYesNo', e.target.checked)}
                      disabled={true}
                      className="h-4 w-4 text-gray-400 border-gray-300 rounded opacity-50 cursor-not-allowed"
                    />
                    <label htmlFor="useAWYesNo" className="ml-2 text-sm text-gray-500">
                      {t('manager.modules.adminHost.warehouseApp.fields.advancedWarehouse', {}, 'Avansert Lager')}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useLocationYesNo"
                      checked={formData.useLocationYesNo}
                      onChange={(e) => handleInputChange('useLocationYesNo', e.target.checked)}
                      disabled={formLoading}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <label htmlFor="useLocationYesNo" className="ml-2 text-sm text-gray-700">
                      {t('manager.modules.adminHost.warehouseApp.fields.useLocation', {}, 'Bruk Lokasjon')}
                    </label>
                  </div>
                </div>
              </div>

              {/* ERP Settings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager.modules.adminHost.warehouseApp.erpSettings', {}, 'ERP-innstillinger')}
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="erpAWYesNo"
                      checked={formData.erpAWYesNo}
                      onChange={(e) => handleInputChange('erpAWYesNo', e.target.checked)}
                      disabled={formLoading}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <label htmlFor="erpAWYesNo" className="ml-2 text-sm text-gray-700">
                      {t('manager.modules.adminHost.warehouseApp.erpAdvancedWarehouse', {}, 'ERP Avansert Lager (lokasjonsbasert eksport)')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">
                    {t('manager.modules.adminHost.warehouseApp.erpAdvancedWarehouseHint', {}, 'Når aktivert eksporteres tellinger med én linje per vare+lokasjon til ERP')}
                  </p>
                </div>
              </div>

              {/* Stock Counting Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager.modules.adminHost.warehouseApp.countingMode', {}, 'Tellemodus')}
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="inStockCountingMode"
                      checked={formData.inStockCountingMode}
                      onChange={(e) => handleInputChange('inStockCountingMode', e.target.checked)}
                      disabled={formLoading}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <label htmlFor="inStockCountingMode" className="ml-2 text-sm text-gray-700">
                      {t('manager.modules.adminHost.warehouseApp.stockCountingMode', {}, 'Lagertelling-modus')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">
                    {t('manager.modules.adminHost.warehouseApp.stockCountingModeHint', {}, 'Når aktivert krever alle tellinger godkjenning fra lagersjef før eksport')}
                  </p>
                </div>
              </div>

              {/* Crossdock (TW 36395306) */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('manager.modules.adminHost.warehouseApp.crossDock.title', {}, 'Crossdock i varemottak')}
                </label>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="grCrossDockingYesNo"
                      checked={formData.grCrossDockingYesNo}
                      onChange={(e) => handleInputChange('grCrossDockingYesNo', e.target.checked)}
                      disabled={formLoading}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                    />
                    <label htmlFor="grCrossDockingYesNo" className="ml-2 text-sm text-gray-700">
                      {t('manager.modules.adminHost.warehouseApp.crossDock.enabled', {}, 'Foreslå crossdock ved restordre')}
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">
                    {t('manager.modules.adminHost.warehouseApp.crossDock.enabledHint', {}, 'Når en vare med restordre tas imot, spør Compass om den skal legges på en crossdock-lokasjon')}
                  </p>

                  {formData.grCrossDockingYesNo && (
                    <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          {t('manager.modules.adminHost.warehouseApp.crossDock.numOfDays', {}, 'Antall dager frem i tid')}
                        </label>
                        <NumericInput
                          mode="integer"
                          value={formData.grCrossDockingNumOfDays}
                          onChange={(e) => handleInputChange('grCrossDockingNumOfDays', e.target.value)}
                          disabled={formLoading}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {t('manager.modules.adminHost.warehouseApp.crossDock.numOfDaysHint', {}, 'Restordre med leveringsdato innen dette antallet dager teller som behov')}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          {t('manager.modules.adminHost.warehouseApp.crossDock.defaultBin', {}, 'Forvalgt crossdock-lokasjon')}
                        </label>
                        <select
                          value={formData.defaultGRCDBinCode}
                          onChange={(e) => handleInputChange('defaultGRCDBinCode', e.target.value)}
                          disabled={formLoading || !editingWarehouse}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          <option value="">{t('manager.modules.adminHost.warehouseApp.crossDock.noDefaultBin', {}, 'Ingen (operatøren velger)')}</option>
                          {formData.defaultGRCDBinCode && !crossDockBins.includes(formData.defaultGRCDBinCode) && (
                            <option value={formData.defaultGRCDBinCode}>{formData.defaultGRCDBinCode}</option>
                          )}
                          {crossDockBins.map(binCode => (
                            <option key={binCode} value={binCode}>{binCode}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {!editingWarehouse
                            ? t('manager.modules.adminHost.warehouseApp.crossDock.defaultBinAfterCreate', {}, 'Kan velges når lageret er opprettet og har crossdock-lokasjoner')
                            : crossDockBins.length === 0
                              ? t('manager.modules.adminHost.warehouseApp.crossDock.noCrossDockBins', {}, 'Lageret har ingen lokasjoner med kategori Crossdocking')
                              : t('manager.modules.adminHost.warehouseApp.crossDock.defaultBinHint', {}, 'Valgfri. Har lageret bare én crossdock-lokasjon, brukes den automatisk')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={formLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {formLoading
                    ? t('common.status.saving', {}, 'Lagrer...')
                    : editingWarehouse
                      ? t('manager.modules.adminHost.warehouseApp.updateWarehouse', {}, 'Oppdater lagersted')
                      : t('manager.modules.adminHost.warehouseApp.createWarehouse', {}, 'Opprett lagersted')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};